"use strict";
/**
 * Copyright 2018 Novage LLC.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HlsJsLoader = void 0;
const debug_1 = __importDefault(require("debug"));
const p2p_media_loader_core_1 = require("@peertube/p2p-media-loader-core");
const byte_range_1 = require("./byte-range");
class HlsJsLoader {
    constructor(segmentManager) {
        this.segmentManager = segmentManager;
        this.debug = (0, debug_1.default)("p2pml:hlsjs-loader");
        this.stats = {
            loaded: 0,
            total: 0,
            aborted: false,
            retry: 0,
            chunkCount: 0,
            bwEstimate: 0,
            loading: {
                start: 0,
                end: 0,
                first: 0,
            },
            parsing: {
                start: 0,
                end: 0,
            },
            buffering: {
                start: 0,
                end: 0,
                first: 0,
            },
        };
        this.boundOnSegmentAbort = this.onSegmentAbort.bind(this);
        this.boundOnUpdateSegmentSize = this.onUpdateSegmentSize.bind(this);
        this.boundOnUpdateLoaded = this.onUpdateLoaded.bind(this);
        this.boundOnSegmentStartLoad = this.onSegmentStartLoad.bind(this);
        this.debugId = "";
    }
    async load(context, _config, callbacks) {
        this.context = context;
        this.callbacks = callbacks;
        HlsJsLoader.updateStatsToStartLoading(this.stats);
        if (this.context.type) {
            this.debug(`Loading playlist ${this.context.url}.`);
            try {
                const result = await this.segmentManager.loadPlaylist(this.context.url);
                this.debug(`Playlist ${this.context.url} loaded.`);
                this.successPlaylist(result, this.context, this.callbacks);
            }
            catch (e) {
                this.error(e, this.context, this.callbacks);
            }
        }
        else if (this.context.frag) {
            this.loader = this.segmentManager.loader;
            this.byteRange = (0, byte_range_1.getByteRange)(this.context);
            this.debugId = this.byteRange
                ? `${this.context.url} / ${this.byteRange.offset}`
                : this.context.url;
            this.debug(`Loading fragment ${this.debugId}.`);
            // We may be downloading the segment by P2P, so we don"t care about the stats sent to HLS ABR
            this.interval = setInterval(() => HlsJsLoader.updateStatsToStartLoading(this.stats), 200);
            this.loader.on(p2p_media_loader_core_1.Events.SegmentAbort, this.boundOnSegmentAbort);
            this.loader.on(p2p_media_loader_core_1.Events.SegmentSize, this.boundOnUpdateSegmentSize);
            this.loader.on(p2p_media_loader_core_1.Events.SegmentStartLoad, this.boundOnSegmentStartLoad);
            try {
                const result = await this.segmentManager.loadSegment(this.context.url, this.byteRange);
                const { content } = result;
                if (content) {
                    this.successSegment(content, this.context, this.callbacks);
                    this.debug(`Loaded fragment ${this.debugId}.`);
                }
                else {
                    this.cleanup();
                    this.debug(`Loaded empty fragment ${this.debugId} (aborted?).`);
                }
            }
            catch (e) {
                setTimeout(() => this.error(e, this.context, this.callbacks), 0);
                this.debug(`Error in fragment ${this.debugId} loading.`, e);
            }
        }
        else {
            console.warn("Unknown load request", this.context);
        }
    }
    abort(context, callbacks) {
        if (this.stats.loading.end || this.stats.aborted)
            return;
        this.debug(`Aborting by hls.js fragment ${this.debugId} loading.`);
        this.cleanup();
        this.segmentManager.abortSegment(context.url, (0, byte_range_1.getByteRange)(context));
        this.stats.aborted = true;
        const onAbort = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onAbort;
        if (onAbort) {
            onAbort(this.stats, context, undefined);
        }
    }
    successPlaylist(xhr, context, callbacks) {
        this.cleanup();
        const now = performance.now();
        this.stats.loading.end = now;
        this.stats.loaded = xhr.response.length;
        this.stats.total = xhr.response.length;
        callbacks.onSuccess({
            url: xhr.responseURL,
            data: xhr.response,
        }, this.stats, context, undefined);
    }
    successSegment(content, context, callbacks) {
        this.cleanup();
        const now = performance.now();
        this.stats.loading.end = now;
        this.stats.loaded = content.byteLength;
        this.stats.total = content.byteLength;
        if (callbacks.onProgress) {
            callbacks.onProgress(this.stats, context, content, undefined);
        }
        callbacks.onSuccess({
            url: context.url,
            data: content,
        }, this.stats, context, undefined);
    }
    error(error, context, callbacks) {
        this.cleanup();
        callbacks.onError(error, context, undefined);
    }
    cleanup() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = undefined;
        }
        if (this.loader) {
            this.loader.off(p2p_media_loader_core_1.Events.SegmentStartLoad, this.boundOnSegmentStartLoad);
            this.loader.off(p2p_media_loader_core_1.Events.SegmentSize, this.boundOnUpdateSegmentSize);
            this.loader.off(p2p_media_loader_core_1.Events.PieceBytesDownloaded, this.boundOnUpdateLoaded);
            this.loader.off(p2p_media_loader_core_1.Events.SegmentAbort, this.boundOnSegmentAbort);
        }
    }
    onSegmentAbort(segment) {
        var _a;
        if (!this.isSegment(segment))
            return;
        this.debug(`Aborting by p2p-media-loader fragment ${this.debugId || ""}.`);
        this.stats.aborted = true;
        const onAbort = (_a = this.callbacks) === null || _a === void 0 ? void 0 : _a.onAbort;
        if (onAbort) {
            onAbort(this.stats, this.context, undefined);
        }
        this.cleanup();
    }
    onUpdateSegmentSize(segment, size) {
        if (!this.isSegment(segment))
            return;
        this.stats.total = size;
    }
    onUpdateLoaded(_type, segment, bytes) {
        if (!this.isSegment(segment))
            return;
        this.stats.loaded += bytes;
    }
    onSegmentStartLoad(method, segment) {
        if (!this.interval || method !== "http" || !this.isSegment(segment))
            return;
        clearInterval(this.interval);
        this.interval = undefined;
        HlsJsLoader.updateStatsToStartLoading(this.stats);
        this.loader.on(p2p_media_loader_core_1.Events.PieceBytesDownloaded, this.boundOnUpdateLoaded);
    }
    ;
    isSegment(segment) {
        return segment.url === this.context.url && segment.range === (0, byte_range_1.byteRangeToString)(this.byteRange);
    }
    static updateStatsToStartLoading(stats) {
        if (stats.aborted)
            return;
        const start = performance.now();
        stats.loading.start = start;
        stats.loading.first = start;
    }
}
exports.HlsJsLoader = HlsJsLoader;
